import React, { useEffect } from "react";
import { MonthCalendar } from "@mtm/components";
import {
  Row,
  Col,
  Typography,
  Space,
  Form,
  Button,
  Image,
  DatePicker,
} from "antd";
import BackCalenderIcon from "../../../assets/icons/calendar.svg";
import CarIcon from "../../../assets/images/carIcon.svg";
import BackArrow from "../../../assets/images/BackArrow.svg";
import Styles from "./ScheduleCalender.module.scss";
import DuplicateSchedule from "./DuplicateSchedule/DuplicateSchedule";
import { useWindowSize } from "../../../hooks/useWindowSize";
import {
  GeneralDefinition,
  TrackAnalytics,
} from "../../../types/ApplicationTypes";
import { useHistory } from "react-router-dom";
import {
  getServiceUserInfoForDashBoardByPersonId,
  getServiceusersInfoByServiceTakerId,
} from "../../../services";
import { useQuery } from "@tanstack/react-query";
import moment, { Moment } from "moment";
import { CloseCircleOutlined } from "@ant-design/icons";
import { useTripsCtx } from "../../../context/TripDetailsContext";
import dotIcon from "../../../assets/icons/dot.svg";
import { backArrowIcon } from "../../../assets/images";
import CustomButton from "../../../common/Buttons/CustomButton";
import { usePlanDetailsCtx } from "../../../context/PlanDetailsContext";
import GoogleLoader from "../../../assets/Loader/GoogleSvgLoader/GoogleLoader";
import { usePaginationCtx } from "../../../context/PaginationContext";
import { usePersonSummaryCtx } from "../../../context/PersonSummaryContext";
import { ITripDetails } from "../../ServiceUsers/Interfaces/ITripDetails";
import { useDashboardCtx } from "../../../context/DashboardContext";

type Props = {
  params: {
    careGiverId: string;
    careTakerId: string;
  };
};

type Date = {
  minDate: string;
  maxDate: string;
  disabledThreeDates: string[];
  currentDate: string;
  defaultStartDate: Moment;
};

const PopOverContent = (e: Date) => {
  const { tripDetailsInfo } = usePlanDetailsCtx();
  TrackAnalytics(
    "/DashBoard/ScheduleService/ScheduleServiceCalender",
    "Short notice trip request",
    "Schedule Service Calender Page"
  );
  return (
    <>
      <Row justify="center" className={Styles.popOverText}>
        <Col>Services must be requested</Col>
        <Col> at least 24 hours before the </Col>
        <Col>service date.</Col>
        <Col span={24}>Call us at</Col>
        <Col> {tripDetailsInfo.wandaCustomerCareNumber} for help.</Col>
      </Row>
    </>
  );
};

const getCalenderDateConfig = (): Date => {
  const day = moment();
  const previousMonth = day.clone().subtract(1, "month");
  const tomorrow = moment()
    .add(1, "day")
    .format("MM/DD/YYYY");
  const minDate: string = previousMonth.format("MM/DD/YYYY");
  const currentDate = day.format("MM/DD/YYYY");

  // Define the starting date
  const startingDate = moment(day, "MM/DD/YYYY");
  const nextYearSameDate = startingDate.clone().add(1, "year");
  const maxDate: string = nextYearSameDate.format("MM/DD/YYYY");

  const disabledThreeDates: string[] = [currentDate, tomorrow];
  const defaultStartDate = moment(tomorrow, "MM/DD/YYYY");

  return {
    minDate,
    maxDate,
    disabledThreeDates,
    currentDate,
    defaultStartDate,
  };
};

const getDisabledPreviousDates = (): string[] => {
  const firstDayOfMonth = moment().startOf("month");
  const todaysDate = moment();
  const futureDate = moment().add(12, "months");
  const lastDayOfMonth = moment(futureDate).endOf("month");
  const disabledDates = [];

  while (todaysDate.diff(firstDayOfMonth, "days") > 0) {
    disabledDates.push(firstDayOfMonth.format("MM/DD/YYYY"));
    firstDayOfMonth.add(1, "day");
  }

  while (futureDate.diff(lastDayOfMonth, "days") < 0) {
    disabledDates.push(lastDayOfMonth.format("MM/DD/YYYY"));
    lastDayOfMonth.subtract(1, "day");
  }
  return disabledDates;
};

const ScheduleCalender: React.FC<Props> = ({ params }) => {
  const { width } = useWindowSize();
  const history = useHistory();
  const { minDate, maxDate, disabledThreeDates } = getCalenderDateConfig();
  const [isDuplicateSchedule, setIsDuplicateSchedule] = React.useState(false);
  const [isDisabledDate, setIsDisabledDate] = React.useState(false);
  const [selectedValidDate, setSelectedValidDate] = React.useState<string>();
  const [isValidData, setIsValidData] = React.useState<{
    value?: string;
    status: boolean;
  }>({ value: undefined, status: false });
  const [shortTripRequested, setShortTripRequested] = React.useState<boolean>(
    false
  );
  const {
    isRebookCtx,
    rebookTripCtx,
    wandaServiceAreaCtx,
    tripScheduleDateCtx,
  } = useTripsCtx();
  const [, setTripScheduleDate] = tripScheduleDateCtx;
  const [isRebook, setIsRebook] = isRebookCtx;
  const [, setRebookTrip] = rebookTripCtx;
  const [, setWandaService] = wandaServiceAreaCtx;
  const { resetPagination } = usePaginationCtx();
  const { isWandaConcierge } = usePersonSummaryCtx();
  const { selectedPersonIdCtx, showTabsCtx } = useDashboardCtx();
  const [selectedPersonId, setSelectedPersonId] = selectedPersonIdCtx;

  const currentCareTakerData = useQuery(
    [
      "getServiceusersInfoByServiceTakerId",
      params.careGiverId,
      params.careTakerId,
    ],
    () =>
      getServiceusersInfoByServiceTakerId(
        Number(params.careGiverId),
        Number(params.careTakerId)
      )
  );

  const serviceUserData = useQuery(
    [
      "getServiceUserInfoForDashBoardByPersonId",
      params.careGiverId,
      params.careTakerId,
    ],
    () =>
      getServiceUserInfoForDashBoardByPersonId(
        Number(params.careGiverId),
        Number(params.careTakerId)
      )
  );

  const handleDateSelected = (date: Date | any) => {
    if (date !== "") {
      setIsDisabledDate(false);
      setSelectedValidDate(date);
      if (date === moment().format("MM/DD/YYYY")) {
        setShortTripRequested(true);
      } else {
        setShortTripRequested(false);
      }
      setIsValidData({ ...isValidData, status: true, value: date });
      const bookedDates =
        serviceUserData.data?.duplicateDates &&
        serviceUserData.data.duplicateDates;
      if (
        bookedDates &&
        bookedDates?.length > 0 &&
        bookedDates?.includes(date)
      ) {
        setIsDuplicateSchedule(true);
        setIsDisabledDate(false);
      }
    }
  };

  const handleClearDate = () => {
    setIsValidData({ ...isValidData, status: false, value: undefined });
  };

  const handleDisabledColumnClick = () => {
    setIsDisabledDate(true);
    setIsValidData({ ...isValidData, status: false, value: undefined });
  };

  const handleCancelDuplicateSchedule = () => {
    setIsDuplicateSchedule(false);
    setIsDisabledDate(false);
    setIsValidData({ ...isValidData, status: false, value: undefined });
  };

  const handleContinueDuplicateSchedule = () => {
    const date = moment(selectedValidDate).format("MM/DD/YYYY");
    setTripScheduleDate(date);
    history.push(
      `/DashBoard/ScheduleService/TripSchedule/${Number(
        params.careGiverId
      )}/${Number(params.careTakerId)}?date=${date}`
    );
  };
  const handleBackButton = () => {
    if (isRebook) {
      setIsRebook(false);
      setRebookTrip([]);
      resetPagination();
      history.push("/DashBoard");
    } else {
      history.push("/DashBoard");
    }
  };

  const handleNextButton = () => {
    let selectedDate: any = selectedValidDate && selectedValidDate;
    const date = moment(selectedDate).format("MM/DD/YYYY");
    const careTakerId = params.careGiverId;
    const serviceUserId = params.careTakerId;
    setWandaService([]);
    history.push(
      `/DashBoard/ScheduleService/TripSchedule/${careTakerId}/${serviceUserId}?date=${date}`
    );
    setTripScheduleDate(date);
  };

  const serviceUserPassToDuplicate = (data: ITripDetails[]) => {
    if (data.length > 0) {
      const currentDate = isValidData.value && isValidData.value;
      const formattedDateString = currentDate?.toString();
      const rs = data.filter(
        (i) =>
          moment(
            i.isWillCall
              ? i.tripDate
              : i.appointmentTime === null
              ? i.requestPickTime
              : i.appointmentTime
          ).format("MM/DD/YYYY") === formattedDateString?.substring(0, 10)
      );
      if (rs) {
        return rs;
      }
    }
  };

  const disabledDate = (current: Moment): boolean => {
    // Disable if the date is before the current date + 1 day for caregiver
    const currentDatePlusOne = moment()
      .add(isWandaConcierge ? 0 : 2, "day")
      .startOf("day");

    if (current && current.isBefore(currentDatePlusOne, "day")) {
      return true;
    }

    // Disable dates beyond 12 months from today
    if (current && current > moment().add(12, "months")) {
      return true;
    }

    return false;
  };

  return width > GeneralDefinition.MobileView ? (
    <>
      {isDuplicateSchedule && (
        <>
          {serviceUserData.data &&
            serviceUserData.data.serviceUserTripInfo.length > 0 && (
              <DuplicateSchedule
                isVisible={isDuplicateSchedule}
                onCancelDuplicateSchedule={handleCancelDuplicateSchedule}
                data={serviceUserPassToDuplicate(
                  serviceUserData.data.serviceUserTripInfo
                )}
                onContinueDuplicateSchedule={handleContinueDuplicateSchedule}
              />
            )}
        </>
      )}
      <Row align="middle">
        <Col offset={4}>
          <span
            onClick={handleBackButton}
            className={Styles.backOptionCalendar}
          >
            <img
              alt="back"
              src={BackArrow}
              className={Styles.backbuttonMargin}
            />
            Back
          </span>
        </Col>
      </Row>
      <Row justify="center">
        <Col>
          <Space direction="vertical">
            <Typography.Text className={Styles.textHeaderBoldBlack}>
              TRANSPORTATION SETUP
            </Typography.Text>
          </Space>
        </Col>
      </Row>
      <Row justify="center" gutter={[0, 50]}>
        <Col span={2} />
        <Col span={20} className={Styles.textHeader}>
          What date does{" "}
          {currentCareTakerData.data && currentCareTakerData.data.firstName}{" "}
          want to be picked up?
        </Col>
        <Col span={2} />
        <Col span={24} />
      </Row>
      <Row justify="center" align="middle" gutter={[0, 25]}>
        <Col>
          <Form size="large" layout="inline">
            <Form.Item>
              <DatePicker
                id={Styles.customInput}
                className={`${Styles.textBoxHeight}`}
                size="large"
                format={"MM/DD/YYYY"}
                defaultValue={undefined}
                value={
                  isValidData.value
                    ? moment(isValidData.value, "MM/DD/YYYY")
                    : undefined
                }
                onChange={(date) =>
                  handleDateSelected(date?.format("MM/DD/YYYY"))
                }
                disabledDate={disabledDate}
                suffixIcon={
                  <Image
                    preview={false}
                    onClick={handleClearDate}
                    src={BackCalenderIcon}
                    width="15px"
                    style={{ cursor: "none" }}
                  />
                }
                clearIcon={
                  <CloseCircleOutlined
                    translate=""
                    onMouseDown={handleClearDate}
                  />
                }
              />
            </Form.Item>
            <Form.Item>
              <Button
                type="primary"
                disabled={!selectedValidDate}
                onClick={handleNextButton}
                className={
                  isValidData.status
                    ? Styles.buttonSelect
                    : Styles.buttonSelect + " wandaBtnDisabled"
                }
              >
                Next
              </Button>
            </Form.Item>
          </Form>
        </Col>
        <Col span={24} />
      </Row>
      {serviceUserData.isLoading || serviceUserData.isFetching ? (
        <GoogleLoader />
      ) : (
        <Row justify="center">
          <Col
            span={13}
            className={`${Styles.desktopCalendarContainer} ${
              Styles.commonCalendarStyles
            }  ${shortTripRequested && Styles.wandaConciergeDesktop}`}
          >
            <MonthCalendar
              language="en"
              minDate={minDate}
              maxDate={maxDate}
              dateClickHandler={(date) =>
                handleDateSelected(date.format("MM/DD/YYYY"))
              }
              defaultDate={isValidData.status ? isValidData.value : undefined}
              disabledDates={isWandaConcierge ? [] : disabledThreeDates}
              useAbbreviations={true}
              visibleTabs={7}
              disableClickHandler={handleDisabledColumnClick}
              disabledCustomPattren={true}
              isDisabledDate={isDisabledDate}
              diabledPopOverContent={(date) => PopOverContent(date)}
              bookedDates={
                serviceUserData.data &&
                serviceUserData.data?.duplicateDates?.length
                  ? serviceUserData.data?.duplicateDates
                  : undefined
              }
              previosDatesDisabled={true}
              previosuDisabledDates={getDisabledPreviousDates()}
              iconColor="blue"
              cellBottomRender={(date) => (
                <Row align="bottom" justify="end">
                  <Col style={{ paddingTop: "35px" }}>
                    <Image preview={false} src={CarIcon} />
                  </Col>
                </Row>
              )}
            />
          </Col>
        </Row>
      )}
    </>
  ) : (
    <>
      {isDuplicateSchedule && (
        <>
          {serviceUserData.data &&
            serviceUserData.data.serviceUserTripInfo.length > 0 && (
              <DuplicateSchedule
                isVisible={isDuplicateSchedule}
                onCancelDuplicateSchedule={handleCancelDuplicateSchedule}
                data={serviceUserPassToDuplicate(
                  serviceUserData.data.serviceUserTripInfo
                )}
                onContinueDuplicateSchedule={handleContinueDuplicateSchedule}
              />
            )}
        </>
      )}
      <Row align="middle" gutter={[0, 38]}>
        <Col offset={2}>
          <CustomButton
            text="Back"
            handlerFunction={handleBackButton}
            type="link"
            icon={backArrowIcon}
            className="backBtn"
          />
        </Col>
        <Col span={24} />
      </Row>
      <Row>
        <Col offset={2}>
          <Space direction="vertical">
            <Typography.Text className={Styles.textHeaderBoldBlackMobile}>
              TRANSPORTATION SETUP
            </Typography.Text>
          </Space>
        </Col>
      </Row>
      <Row gutter={[0, 30]}>
        <Col span={2} />
        <Col span={20}>
          <Space direction="vertical" size={0}>
            <Typography.Text className={Styles.textHeaderBold30}>
              What date does{" "}
              {currentCareTakerData.data && currentCareTakerData.data.firstName}{" "}
              want to be picked up?
            </Typography.Text>
          </Space>
        </Col>
        <Col span={2} />
        <Col span={24} />
      </Row>
      <Row justify="center" align="middle" gutter={[0, 30]}>
        <Col className={Styles.mobileDatePickerLayout}>
          <Form layout="inline">
            <Form.Item>
              <DatePicker
                id={Styles.customInput}
                className={`${Styles.textBoxHeight}`}
                size="large"
                format={"MM/DD/YYYY"}
                defaultValue={undefined}
                value={
                  isValidData.value
                    ? moment(isValidData.value, "MM/DD/YYYY")
                    : undefined
                }
                onChange={(date) =>
                  handleDateSelected(date?.format("MM/DD/YYYY"))
                }
                disabledDate={disabledDate}
                suffixIcon={
                  <Image
                    preview={false}
                    onClick={handleClearDate}
                    src={BackCalenderIcon}
                    width="15px"
                    style={{ cursor: "none" }}
                  />
                }
                clearIcon={
                  <CloseCircleOutlined
                    translate=""
                    onMouseDown={handleClearDate}
                    width="15px"
                  />
                }
              />
            </Form.Item>
          </Form>
        </Col>
        <Col span={24} />
      </Row>
      {serviceUserData.isLoading || serviceUserData.isFetching ? (
        <GoogleLoader />
      ) : (
        <Row justify="center">
          <Col
            className={`${Styles.mobileCalendarContainer} ${
              Styles.commonCalendarStyles
            } ${shortTripRequested && Styles.wandaConciergeMobile}`}
          >
            <MonthCalendar
              language="en"
              minDate={minDate}
              maxDate={maxDate}
              defaultDate={isValidData.status ? isValidData.value : undefined}
              dateClickHandler={(date) =>
                handleDateSelected(date.format("MM/DD/YYYY"))
              }
              disabledDates={isWandaConcierge ? [] : disabledThreeDates}
              visibleTabs={3}
              mobileView={true}
              disableClickHandler={handleDisabledColumnClick}
              disabledCustomPattren={true}
              isDisabledDate={isDisabledDate}
              diabledPopOverContent={(date) => PopOverContent(date)}
              bookedDates={
                serviceUserData.data &&
                serviceUserData.data?.duplicateDates?.length
                  ? serviceUserData.data?.duplicateDates
                  : undefined
              }
              previosDatesDisabled={true}
              previosuDisabledDates={getDisabledPreviousDates()}
              iconColor="blue"
              cellBottomRender={(date) => (
                <Row justify="end">
                  <Col>
                    <Image preview={false} src={dotIcon} width="5px" />
                  </Col>
                </Row>
              )}
            />
          </Col>
        </Row>
      )}
      <Row justify="center" align="middle" gutter={[0, 30]}>
        <Col span={24} />
        <Col>
          <Form layout="inline">
            <Form.Item>
              <Button
                type="primary"
                disabled={!selectedValidDate}
                onClick={handleNextButton}
                className={
                  isValidData.status
                    ? Styles.buttonSelect
                    : Styles.buttonSelect + " wandaBtnDisabled"
                }
              >
                Next
              </Button>
            </Form.Item>
          </Form>
        </Col>
      </Row>
    </>
  );
};

export default ScheduleCalender;
